import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CONFIG } from "../../app-config";
import SidebarHeader from "../../components/SidebarHeader";
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from "../../utils/api";
import { FaMoneyBillWave, FaStore, FaExchangeAlt } from 'react-icons/fa';
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

function AgentCommission() {
    const [commissionData, setCommissionData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [perPage] = useState(15);
    const [summaryData, setSummaryData] = useState({
        agent_profit: null,
        agent_withdrawal: null,
        available_limit: null
    });
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchSummary();
    }, [navigate]);

    useEffect(() => {
        fetchCommissionData(currentPage);
    }, [currentPage]);

    const fetchSummary = async () => {
        try {
            const result = await apiCall(
                "agent-profit-summary",
                {},
                true
            );

            if (result.status) {
                setSummaryData({
                    agent_profit: result.agent_profit || "0",
                    agent_withdrawal: result.agent_withdrawal || "0",
                    available_limit: result.available_limit || "0",
                });
            } else {
                toast.error(result.message);
            }
        } catch (err) {
            toast.error(err.message);
            console.error("Error fetching commission:", err);
        }
    };

    const fetchCommissionData = async (page) => {
        setLoading(true);
        setError("");

        try {
            const result = await apiCall(
                "agent-profit",
                {
                    page: page,
                },
                true
            );

            if (result.status) {
                setCommissionData(result.commission || []);
                setTotalPages(result.total_pages || 1);
                setTotalRecords(result.total || 0);
            } else {
                setError(result.message || "Failed to fetch commission data");
            }
        } catch (err) {
            setError("Network error. Please try again.");
            console.error("Error fetching commission:", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const total = totalPages;

        if (total <= 5) {
            for (let i = 1; i <= total; i++) {
                pageNumbers.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 5; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(total);
            } else if (currentPage >= total - 2) {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = total - 4; i <= total; i++) {
                    pageNumbers.push(i);
                }
            } else {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(total);
            }
        }
        return pageNumbers;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return dateStr;
    };

    const formatAmount = (amount) => {
        if (amount === undefined || amount === null) return '₹0.00';
        return `₹${parseFloat(amount).toFixed(2)}`;
    };

    const handleWithdrawSubmit = async () => {
        if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }

        if (parseFloat(withdrawAmount) > parseFloat(summaryData.available_limit)) {
            toast.error("Insufficient balance");
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                amount: withdrawAmount
            };

            const result = await apiCall(
                "agent-withdraw",
                payload,
                true
            );

            if (result.status) {
                toast.success("Withdrawal request submitted successfully!");
                closeModal();
                fetchSummary();
            } else {
                toast.error(result.message || "Withdrawal failed");
            }
        } catch (err) {
            toast.error(err.message || "Network error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowWithdrawModal(false);
            setIsClosing(false);
            setWithdrawAmount("");
        }, 300);
    };

    const LoadingSpinner = () => (
        <div className="text-center py-12">
            <div
                className="w-10 h-10 rounded-full animate-spin mx-auto mb-4"
                style={{
                    border: `3px solid ${COLORS.primaryColor}33`,
                    borderTopColor: COLORS.primaryColor
                }}
            />
            <div
                className="text-sm font-medium"
                style={{
                    color: COLORS.textSecondary,
                }}
            >
                Loading commission...
            </div>
        </div>
    );


    return (
        <SidebarHeader>
            <div className="flex items-center gap-3 pb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                >
                    <FiArrowLeft className="text-xl" />
                </button>
                <h1 className="text-lg font-semibold text-gray-800">
                    Agent - Commission
                </h1>
            </div>
            <div className="max-w-7xl mx-auto mb-16">
                {/* Summary Card */}
                <div className="rounded-xl overflow-hidden mb-4 shadow-lg">
                    <div className="flex">
                        {/* Commission Section - Green */}
                        <div className="flex-1 p-4" style={{ background: '#10b981' }}>
                            <div className="text-white/80 text-xs font-medium uppercase tracking-wider">
                                Total Commission
                            </div>
                            <div className="text-white text-xl font-bold mt-1">
                                {formatAmount(summaryData.agent_profit)}
                            </div>
                        </div>

                        {/* Divider */}
                        <div style={{ width: '2px', background: 'rgba(255,255,255,0.3)' }}></div>

                        {/* Withdrawal Section - Red */}
                        <div className="flex-1 p-4" style={{ background: '#ef4444' }}>
                            <div className="text-white/80 text-xs font-medium uppercase tracking-wider">
                                Total Withdrawal
                            </div>
                            <div className="text-white text-xl font-bold mt-1">
                                {summaryData.agent_withdrawal !== null
                                    ? formatAmount(summaryData.agent_withdrawal)
                                    : '₹0.00'}
                            </div>
                        </div>
                    </div>

                    {/* Withdraw Button */}
                    <button
                        onClick={() => setShowWithdrawModal(true)}
                        className="w-full py-3 font-semibold text-sm transition-colors"
                        style={{
                            background: COLORS.primaryColor,
                            color: COLORS.textOnPrimary
                        }}
                    >
                        Withdraw Now - {formatAmount(summaryData.available_limit)}
                    </button>
                </div>

                {/* Commission Table */}
                <div className="flex-1">
                    {loading ? (
                        <LoadingSpinner />
                    ) : error ? (
                        <div className="text-center py-8" style={{ color: COLORS.errorColor }}>
                            {error}
                        </div>
                    ) : commissionData.length === 0 ? (
                        <div className="text-center py-8" style={{ color: COLORS.textSecondary }}>
                            No commission records found
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border"
                            style={{ borderColor: COLORS.borderColor }}
                        >
                            <table className="w-full text-sm">
                                <thead>
                                    <tr style={{
                                        background: COLORS.primaryColor,
                                        color: COLORS.textOnPrimary
                                    }}>
                                        <th className="px-4 py-3 text-left font-semibold">#</th>
                                        <th className="px-4 py-3 text-left font-semibold">Operator</th>
                                        <th className="px-4 py-3 text-left font-semibold">Commission</th>
                                        <th className="px-4 py-3 text-left font-semibold">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {commissionData.map((item, index) => (
                                        <tr
                                            key={item.time || index}
                                            className="border-b last:border-b-0 transition-colors hover:opacity-80"
                                            style={{
                                                borderColor: COLORS.borderColor,
                                                background: index % 2 === 0 ? COLORS.surfaceColor : 'transparent'
                                            }}
                                        >
                                            <td className="px-4 py-3" style={{ color: COLORS.textPrimary }}>
                                                {(currentPage - 1) * perPage + index + 1}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {item.logo && (
                                                        <img
                                                            src={item.logo}
                                                            alt={item.biller_name}
                                                            className="w-6 h-6 rounded object-cover"
                                                            onError={(e) => e.target.style.display = 'none'}
                                                        />
                                                    )}
                                                    <span style={{ color: COLORS.textPrimary }}>
                                                        {item.biller_name || 'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 font-semibold" style={{ color: COLORS.primaryColor }}>
                                                {formatAmount(item.agent_profit)}
                                            </td>
                                            <td className="px-4 py-3" style={{ color: COLORS.textPrimary }}>
                                                {formatDate(item.date)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!loading && !error && totalPages > 1 && (
                    <div className="mt-6 flex justify-center">
                        <div className="flex gap-2 items-center">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                style={{
                                    background: COLORS.surfaceColor,
                                    color: COLORS.textPrimary,
                                    border: `1px solid ${COLORS.borderColor}`
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>

                            <div className="flex gap-1">
                                {getPageNumbers().map((page, index) => (
                                    page === '...' ? (
                                        <span
                                            key={`ellipsis-${index}`}
                                            className="px-3 py-1.5 text-sm"
                                            style={{ color: COLORS.textSecondary }}
                                        >
                                            ...
                                        </span>
                                    ) : (
                                        <button
                                            key={page}
                                            onClick={() => handlePageChange(page)}
                                            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200"
                                            style={{
                                                background: currentPage === page ? COLORS.primaryColor : COLORS.surfaceColor,
                                                color: currentPage === page ? COLORS.textOnPrimary : COLORS.textPrimary,
                                                border: `1px solid ${currentPage === page ? COLORS.primaryColor : COLORS.borderColor}`
                                            }}
                                        >
                                            {page}
                                        </button>
                                    )
                                ))}
                            </div>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                style={{
                                    background: COLORS.surfaceColor,
                                    color: COLORS.textPrimary,
                                    border: `1px solid ${COLORS.borderColor}`
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                {/* Show total records info */}
                {!loading && !error && totalRecords > 0 && (
                    <div className="mt-4 text-center text-xs" style={{ color: COLORS.textSecondary }}>
                        Showing {(currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} records
                    </div>
                )}
            </div>

            {/* Withdraw Centered Modal with Fade Animation */}
            {showWithdrawModal && (
                <div
                    className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
                    onClick={closeModal}
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-black/50"></div>

                    {/* Modal Content */}
                    <div
                        className={`relative w-full max-w-md mx-4 p-6 rounded-2xl transform transition-all duration-300 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}
                        style={{
                            background: COLORS.surfaceColor || '#ffffff',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors"
                            style={{ color: COLORS.textSecondary }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>
                            Withdraw Funds
                        </h3>

                        {/* Available Balance */}
                        <div className="mb-4 p-3 rounded-lg" style={{ background: COLORS.primaryColor + '10' }}>
                            <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                Available Balance
                            </div>
                            <div className="text-xl font-bold" style={{ color: COLORS.primaryColor }}>
                                {formatAmount(summaryData.available_limit)}
                            </div>
                        </div>

                        {/* Amount Field */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.textPrimary }}>
                                Enter Amount (₹)
                            </label>
                            <input
                                type="number"
                                value={withdrawAmount}
                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                placeholder="Enter amount"
                                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                                style={{
                                    borderColor: COLORS.borderColor,
                                    background: COLORS.backgroundColor || '#f9fafb',
                                    color: COLORS.textPrimary,
                                    fontSize: '16px'
                                }}
                                autoFocus
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={closeModal}
                                className="flex-1 py-3 rounded-lg font-medium transition-colors"
                                style={{
                                    background: '#e5e7eb',
                                    color: COLORS.textPrimary
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleWithdrawSubmit}
                                disabled={isSubmitting}
                                className="flex-1 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                style={{
                                    background: COLORS.primaryColor,
                                    color: COLORS.textOnPrimary
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg
                                            className="animate-spin h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    'Withdraw'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Toaster />
        </SidebarHeader>
    );
}

export default AgentCommission;